import { PageEntities, PageState } from './page.reducer';
import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import * as fromChart from '../chart/chart.selectors';
import { ChartOption, PageConfig, PredefinedCharts, GlobalFilter } from './page.models';
import { pipe } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TableConfig } from '../../../models/chart.models';
import { getPredefinedTotal } from 'shared/features/ng-redux/utils/utils';
import { ReportPageBaselineId } from '@nx-fe/reports';

export const getPageState = createFeatureSelector<PageState>('pages');

export const getGlobalFilter = createSelector(getPageState, (pageState: PageState): GlobalFilter => {
  return pageState.globalFilter;
});

export const getPagesEntities = createSelector(getPageState, (pageState: PageState): PageEntities => {
  return pageState.entities;
});

export const getPages = createSelector(getPagesEntities, (entities: PageEntities): PageConfig[] => {
  return Object.keys(entities).map(key => entities[key]);
});

export const getIcuPage = createSelector(getPages, (entities: PageConfig[]): PageConfig => {
  return entities.find(page => page.baselineId === 'watchpage');
});

export const getPagesFiltered = pipe(
  select(getPages),
  filter(pages => pages && pages.length > 0)
);
export const getPageById = (pageId: string) => {
  return createSelector(getPagesEntities, (pageEntities: PageEntities) => {
    return pageEntities[pageId];
  });
};

export const getPageByBaselineId = (pageId: ReportPageBaselineId) => {
  return createSelector(getPagesEntities, (pageEntities: PageEntities) => {
    return Object.values<PageConfig>(pageEntities).filter(v => v.baselineId === pageId);
  });
};

export const getCurrentPageId = createSelector(getPageState, (pageState: PageState) => {
  return pageState.currentPageId;
});

export const getCurrentShiftId = createSelector(getPageState, (pageState: PageState) => {
  return pageState.entities[pageState.currentPageId].shiftId;
});

export const getCurrentPage = createSelector(getPageState, (pageState: PageState) => {
  return pageState.entities[pageState.currentPageId];
});

export const getCurrentPageFiltered = pipe(
  select(getCurrentPage),
  filter(val => !!val)
);

export const getPredefinedChartsStateByPageId = (pageId: string) => {
  return createSelector(getPageById(pageId), (currPage: PageConfig) => {
    if (!currPage) {
      return [];
    }
    return currPage.predefinedCharts || [];
  });
};

export const getCurrentPagePredefinedChartsTotal = createSelector(getCurrentPage, page => {
  if (page) {
    return getPredefinedTotal(page.predefinedCharts);
  }
  return 0;
});
export const getCurrentPagePredefinedCharts = createSelector(getCurrentPage, page => {
  if (page) {
    return page.predefinedCharts;
  }
  return [];
});

export const allCurrentPageCharts = createSelector(
  getCurrentPage,
  fromChart.getChartsEntities,
  (page, chartEntities) => {
    if (page) {
      const chartIds = page.chartIds || [];
      return chartIds.map(chartId => chartEntities[chartId]);
    }
  }
);

export const getCurrentCharts = createSelector(getCurrentPage, fromChart.getAllCharts, (page, charts) => {
  if (!page || !page.chartIds) {
    return [];
  }

  const predefinedNotSelectedChartIds = [];
  page.predefinedCharts?.forEach((c: PredefinedCharts) => {
    c.charts.forEach((r: ChartOption) => {
      if (!r.isSelected) predefinedNotSelectedChartIds.push(r.chartId);
    });
  });

  return charts.filter(chart => page.chartIds.includes(chart.id) && !predefinedNotSelectedChartIds.includes(chart.id));
});

export const getCurrentPageCascadingFilters = createSelector(getCurrentPage, getCurrentCharts, (page, charts) => {
  if (!page) return null;
  return { cascadingFilters: page.cascadingFilters, chartFilters: charts.map(chart => chart.filters) };
});

export const getCascadingFiltersByPageId = (pageId: string) => {
  return createSelector(getPageById(pageId), getSelectedChartsByPageId(pageId), (page, charts) => {
    if (!page) return null;
    return { cascadingFilters: page.cascadingFilters, chartFilters: charts.map(chart => chart.filters) };
  });
};

export const getSelectedChartsByPageId = (pageId: string) => {
  return createSelector(getPageById(pageId), fromChart.getAllCharts, (page, charts) => {
    if (!page) {
      return [];
    }

    const predefinedNotSelectedChartIds = [];
    if (page.predefinedCharts) {
      page.predefinedCharts.forEach((c: PredefinedCharts) => {
        c.charts.forEach((r: ChartOption) => {
          if (!r.isSelected) predefinedNotSelectedChartIds.push(r.chartId);
        });
      });
    }

    return charts.filter(
      chart => page.chartIds?.includes(chart.id) && !predefinedNotSelectedChartIds.includes(chart.id)
    );
  });
};

export const getAllChartsByPageId = (pageId: string) => {
  return createSelector(getPageById(pageId), fromChart.getAllCharts, (page, charts) => {
    if (!page) {
      return [];
    }

    return charts.filter(chart => page.chartIds.includes(chart.id));
  });
};

export const getCurrentChartsFiltered = pipe(
  select(getCurrentCharts),
  filter(charts => charts && charts.length > 0)
);

export const getCurrentTables = createSelector(getCurrentPage, fromChart.getAllCharts, (page: PageConfig, charts) => {
  if (!page) {
    return [];
  }
  const tableCharts = charts.filter(chart => page.chartIds?.includes(chart.id) && chart.elementType === 'table');
  return tableCharts as TableConfig[];
});

export const getCurrentTablesFiltered = pipe(
  select(getCurrentTables),
  filter(charts => !!charts)
);

export const getPageMCGroup = createSelector(getCurrentPage, page => {
  if (!page) return null;
  return page.mcGroups?.filter(group => group.hasGroupAccess);
});

export const getPageStores = createSelector(getCurrentPage, page => {
  if (!page) return null;
  return page.stores;
});

export const getPageSelectedStores = createSelector(getCurrentPage, page => {
  if (!page) return null;
  return page.selectedStores;
});

export const getPageSelectedDateFilter = createSelector(getCurrentPage, page => {
  if (!page) return null;
  return page.selectedDateFilter;
});

export const getSpecificPageSelectedDateFilter = (pageId: string) => {
  return createSelector(getPagesEntities, (pageEntities: PageEntities) => {
    return pageEntities[pageId].selectedDateFilter;
  });
};

export const getSelectedTab = createSelector(getCurrentPage, (currPage: PageConfig) => {
  if (!currPage) {
    return null;
  }
  return currPage.selectedTab;
});

export let currentPageFilterBarConfig = createSelector(getCurrentPage, page => {
  if (!page) {
    return undefined;
  }
  return page.filterBarConfig;
});

export let currentPageFilterBarExpression = createSelector(getCurrentPage, (page: PageConfig) => {
  if (!page) {
    return undefined;
  }
  return page.filterBarExpression;
});

export let icuPageFilterBarExpression = createSelector(getIcuPage, (page: PageConfig) => {
  if (!page) {
    return undefined;
  }
  return page.filterBarExpression;
});

export const currentPageId = createSelector(getPageState, (pageState: PageState) => {
  if (pageState && pageState.currentPageId) {
    return pageState.currentPageId;
  }
});

export const currentPageBaselineId = createSelector(getPageState, (pageState: PageState) => {
  const page: PageConfig = pageState.entities[pageState.currentPageId];
  if (page) {
    return page.baselineId;
  }
});

export const currentPageApplicationType = createSelector(getPageState, (pageState: PageState) => {
  const page: PageConfig = pageState.entities[pageState.currentPageId];
  if (page) {
    return page.applicationType[0];
  }
});

export const getPageChartOfElementType = (pageId: string, elementType: string) =>
  createSelector(getPagesEntities, fromChart.getChartsEntities, (pageEntities, chartEntities) => {
    const page = pageEntities[pageId];
    const chartIds = page.chartIds || [];
    return chartIds.map(cId => chartEntities[cId]).find(ch => ch.elementType === elementType);
  });
